import React, { useEffect, useState } from 'react'
import { isNativeApp } from '../../utils/nativeApp'
import { openOAuthProvider } from '../../utils/oauthStart'
import styles from './oauth-buttons.module.scss'

// Matches OauthLoginsController::CARRIED_PARAM_KEYS.
const CARRIED_PARAM_KEYS = [
    'space_invite',
    'stream_invite',
    'stream_join',
    'prospect_token',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'gbraid',
    'wbraid',
    'landing_page',
    'landing_button',
    'landing_page_url',
    'landing_page_host',
    'landing_page_path',
    'landing_page_search',
    'landing_referrer',
    'amplitude_device_id',
]

const PROVIDER_LABELS = { google: 'Google', x: 'X' }

const OAUTH_ERROR_MESSAGES = {
    access_denied: 'Sign-in was cancelled.',
    account_unavailable: 'That account is no longer available.',
    provider_unavailable: 'That sign-in option is not available right now.',
}
const DEFAULT_OAUTH_ERROR = 'Could not sign you in. Please try again.'

export function oauthErrorMessage(code) {
    return OAUTH_ERROR_MESSAGES[code] || DEFAULT_OAUTH_ERROR
}

function GoogleIcon() {
    return (
        <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
            <path
                fill="#4285F4"
                d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
            />
            <path
                fill="#34A853"
                d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.33-1.58-5.04-3.7H.96v2.33A9 9 0 0 0 9 18z"
            />
            <path
                fill="#FBBC05"
                d="M3.96 10.72A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.18.28-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3-2.33z"
            />
            <path
                fill="#EA4335"
                d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3 2.33C4.67 5.16 6.66 3.58 9 3.58z"
            />
        </svg>
    )
}

function XIcon() {
    return (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="currentColor">
            <path d="M18.24 2.25h3.31l-7.23 8.26 8.5 11.24h-6.66l-5.21-6.82-5.97 6.82H1.67l7.73-8.84L1.25 2.25h6.83l4.71 6.23 5.45-6.23zm-1.16 17.52h1.83L7.08 4.13H5.12l11.96 15.64z" />
        </svg>
    )
}

export default function OAuthButtons({ mode, embedded = false, attribution = {}, onStart }) {
    const [providers, setProviders] = useState(/** @type {string[]} */ ([]))

    useEffect(() => {
        let cancelled = false
        fetch(`${window.knovApiUrl}/api/v1/gon/signup`, { headers: { Accept: 'application/json' } })
            .then(response => (response.ok ? response.json() : null))
            .then(config => {
                if (!cancelled && Array.isArray(config?.OAUTH_PROVIDERS)) {
                    setProviders(config.OAUTH_PROVIDERS.filter(p => PROVIDER_LABELS[p]))
                }
            })
            .catch(() => {})
        return () => {
            cancelled = true
        }
    }, [])

    if (providers.length === 0) return null

    const startOAuth = provider => {
        onStart?.(provider)

        // The round trip lands on /login or /signup; bring a modal user back here.
        if (embedded) {
            try {
                if (!sessionStorage.getItem('attempted_path')) {
                    sessionStorage.setItem(
                        'attempted_path',
                        `${window.location.pathname}${window.location.search}${window.location.hash}`,
                    )
                }
            } catch (_error) {
                // sessionStorage unavailable; fall back to the default destination.
            }
        }

        const urlParams = new URLSearchParams(window.location.search)
        const query = new URLSearchParams()
        CARRIED_PARAM_KEYS.forEach(key => {
            const value = attribution?.[key] || urlParams.get(key)
            if (value) query.set(key, value)
        })
        // The provider page finishes in Safari or Chrome; the API uses this to
        // send the person back to the app.
        if (isNativeApp()) query.set('client', 'app')
        const queryString = query.toString()
        openOAuthProvider(
            `${window.knovApiUrl}/oauth/${provider}${queryString ? `?${queryString}` : ''}`,
        )
    }

    return (
        <div className={styles.oauthSection}>
            <div className={styles.oauthButtons}>
                {providers.map(provider => (
                    <button
                        key={provider}
                        type="button"
                        className={styles.oauthButton}
                        onClick={() => startOAuth(provider)}
                        data-testid={`oauth-${provider}-button`}
                    >
                        {provider === 'google' ? <GoogleIcon /> : <XIcon />}
                        <span>Continue with {PROVIDER_LABELS[provider]}</span>
                    </button>
                ))}
            </div>
            <div className={styles.divider}>
                <span>{mode === 'signup' ? 'or sign up with email' : 'or log in with email'}</span>
            </div>
        </div>
    )
}
